import isEmpty from 'lodash/isEmpty.js'
const LearningContinuumHelper = {}

const norm = (s) =>
  String(s ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')

LearningContinuumHelper.norm = norm

LearningContinuumHelper.parsedContent = (headers = [], content = [], options = {}) => {
  const { createMissing = false } = options
  const structureHeaders = headers.filter((h) => h.type === 'structure')

  return content.map((original) => {
    const row = { ...original, isValid: true, error: {}, newElements: [] }

    const resolvedId = {} // keyName -> _id del elemento existente
    const isNew = {} // keyName -> true si hay que crearlo
    const chain = {} // keyName -> [{ structureId, name }] desde la learning area

    // newHeaders se arma en profundidad: el padre siempre se procesa antes que sus hijos
    structureHeaders.forEach((header) => {
      const value = String(row[header.keyName] ?? '').trim()
      if (!value) return

      const parent = structureHeaders.find((h) => h.keyName === header.parentKeyName)

      if (parent) {
        if (row.error[parent.name]) return // el padre ya falló
        if (!chain[parent.keyName]) {
          row.error[header.name] = `"${parent.name}" is required when "${header.name}" has a value`
          return
        }
      }

      const parentChain = parent ? chain[parent.keyName] : []
      const parentIsNew = parent ? isNew[parent.keyName] : false

      // Buscar solo entre los hijos del padre ya resuelto
      const found =
        !parentIsNew &&
        header.children.find(
          (c) =>
            norm(c.elementName) === norm(value) &&
            (!parent || String(c.parent?.elementId) === String(resolvedId[parent.keyName]))
        )

      if (found) {
        resolvedId[header.keyName] = found.elementId
        row[header.keyName] = found.elementName // nombre exacto, como lo espera el repository
        chain[header.keyName] = [...parentChain, { structureId: header.structureId, name: found.elementName }]
        return
      }

      // La learning area (primer nivel) siempre debe existir
      if (!parent) {
        row.error[header.name] = `Value: "${value}" is not valid`
        return
      }

      if (!createMissing) {
        if (header.children.length) {
          row.error[header.name] = `Value: "${value}" is not valid`
        } else {
          // Nivel sin elementos en el curriculum: no se valida (igual que hoy)
          chain[header.keyName] = [...parentChain, { structureId: header.structureId, name: value }]
        }
        return
      }

      // Elemento nuevo
      isNew[header.keyName] = true
      chain[header.keyName] = [...parentChain, { structureId: header.structureId, name: value }]
      row[header.keyName] = value
      row.newElements.push({
        level: header.name,
        name: value,
        parentName: parentChain[parentChain.length - 1].name,
        path: chain[header.keyName]
      })
    })

    row.isValid = Object.keys(row.error).length === 0
    return row
  })
}

// Elementos únicos a crear en todo el archivo (dedup por ruta completa)
LearningContinuumHelper.collectNewElements = (rows = []) => {
  const map = new Map()
  rows
    .filter((r) => r.isValid)
    .forEach((row) =>
      row.newElements.forEach((el) => {
        const key = el.path.map((p) => `${p.structureId}:${norm(p.name)}`).join('>')
        if (!map.has(key)) map.set(key, el)
      })
    )
  return [...map.values()]
}

LearningContinuumHelper.parsedHeader = (fileData = [], currentCurriculum = {}) => {
  const { elementsStructure, elements, lcElements, levels } = currentCurriculum
  const headers = fileData[0]
  const lines = fileData.slice(1)
  const content =
    lines
      ?.map((row) => {
        const values = row || []
        return headers.reduce((acc, key, i) => {
          acc[key] = values[i] ?? ''
          return acc
        }, {})
      })
      ?.filter((row) => Object.values(row).some((value) => String(value).trim() !== '')) || []

  let newHeaders = []
  // check is valid header
  let isValidHeader = true

  // check if structure elements in header are valid
  const filterFileStructure = headers.filter((h) => h.indexOf('structure_') === 0)
  if (!filterFileStructure.length) {
    isValidHeader = false
  }
  let structureChild = {}

  const findStruncture = (child, fileStructure, elements = [], parent = null) => {
    const element = elements?.find((el) => String(el.elementStructureId) === String(child._id))
    const keyName = `structure_${String(child.name)}`
    const find = fileStructure.find((h) => h === keyName)
    if (find) {
      structureChild[keyName] = true
      let childrenElements = []
      let newElements = []
      if (String(element?.elementStructureId) === String(child?._id)) {
        element.children?.forEach((el) => {
          let model = {
            keyName,
            keyId: child._id,
            elementName: el.name,
            elementId: el._id,
            parent: el.model // undefined en el primer nivel
          }

          let elements = []
          if (el.elements?.length) {
            el.elements.forEach((c) => {
              elements.push(c)
              const finddNewElements = newElements.find(
                (e) => String(e.elementStructureId) === String(c.elementStructureId)
              )
              if (!finddNewElements) {
                newElements.push({
                  elementStructureId: c.elementStructureId,
                  children: c.children.map((child) => ({
                    model,
                    ...child
                  }))
                })
              } else {
                newElements = newElements.map((e) => {
                  if (String(e.elementStructureId) === String(c.elementStructureId)) {
                    return {
                      ...e,
                      children: e.children.concat(
                        c.children.map((child) => ({
                          model,
                          ...child
                        }))
                      )
                    }
                  }
                  return e
                })
              }
            })
          }

          childrenElements.push({
            ...model,
            elements
          })
        })
      }

      newHeaders.push({
        keyName,
        key: `elementsStructure.${String(child._id)}`,
        structureId: String(child._id),
        parentKeyName: parent?.keyName ?? null,
        name: child.name,
        children: childrenElements,
        mandatory: child.mandatory,
        type: 'structure'
      })

      if (child.children?.length) {
        const newFileStructure = fileStructure.filter((h) => h !== keyName)
        child.children.forEach((c) => {
          findStruncture(c, newFileStructure, newElements, { keyName })
        })
      }
    } else {
      structureChild[keyName] = false
    }
  }

  findStruncture(elementsStructure, filterFileStructure, [elements])
  if (Object.keys(structureChild).some((key) => !structureChild[key])) {
    isValidHeader = false
  }

  // check if lc elements in header are valid
  const filterFileLcElements = headers.filter((h) => h.indexOf('lcElement_') === 0)
  if (!filterFileLcElements.length) {
    isValidHeader = false
  }
  lcElements?.children.forEach((s) => {
    const keyName = `lcElement_${String(s.name)}`
    const find = filterFileLcElements.find((h) => h === keyName)
    if (!find) {
      isValidHeader = false
    } else {
      newHeaders.push({
        keyName,
        key: `lcElements.${String(s._id)}`,
        name: s.name,
        mandatory: true,
        children: [1],
        type: 'lcElement'
      })
    }
  })

  // check if levels in header are valid
  const filterFileLevels = headers.filter((h) => h.indexOf('levels_') === 0)
  if (!filterFileLevels.length) {
    isValidHeader = false
  }
  levels.forEach((lvl) => {
    const find = filterFileLevels.find((h) => h === `levels_${String(lvl.name)}`)
    if (!find) {
      isValidHeader = false
    } else {
      newHeaders.push({
        key: `levels.${String(lvl._id)}`
      })
    }
  })
  return { isValidHeader, headers: newHeaders, content }
}

// LearningContinuumHelper.parsedContent = (headers = [], content = []) => {
//   return content.map((row) => {
//     row.isValid = true;
//     row.error = {};
//     headers.forEach((header) => {
//       if (row[header.keyName]) {
//         if (header.children?.length && header.type === 'structure') {
//           const find = header.children.find(
//             (child) => child.elementName === row[header.keyName]
//           );
//           if (!find) {
//             row.error[header.name] =
//               `Value: "${row[header.keyName]}" is not valid`;
//           }
//         }
//       } else if (
//         header.mandatory &&
//         String(row[header.keyName]).trim() === '' &&
//         header.children?.length
//       ) {
//         // row.error[header.name] = 'This field is required';
//       }
//     });

//     if (Object.keys(row.error).length > 0) {
//       row.isValid = false;
//     }

//     return row;
//   });
// };

LearningContinuumHelper.checkCurriculumElements = (lcData, currentCurriculum) => {
  let isValid = true
  let error = {}

  const mapCheck = (elements, structures, form) => {
    let errors = {}

    structures.forEach((structure) => {
      const element = elements.find((el) => String(el.elementStructureId) === String(structure._id)) || {
        children: []
      }

      if (structure.mandatory && element.children.length > 0) {
        const newForm = form[structure._id]

        if (newForm) {
          if (!newForm.value) {
            errors[structure.name] = `This field is required.`
          } else {
            const newElements = element.children.find((el) => String(el._id) === String(newForm.value))

            errors = {
              ...errors,
              ...mapCheck(newElements.elements || [], structure.children, newForm.children)
            }
          }
        } else {
          errors[structure.name] = `This field is required.`
        }
      }
    })

    return errors
  }
  if (isEmpty(lcData.curriculumElements)) {
    isValid = false
    error['curriculumElements'] = 'Curriculum elements are required'
  } else {
    const message = mapCheck(
      [currentCurriculum.elements],
      [currentCurriculum.elementsStructure],
      lcData.curriculumElements
    )
    if (message && Object.keys(message).length > 0) {
      isValid = false
      error = {
        ...error,
        ...message
      }
    }
  }
  return {
    isValid,
    error
  }
}

LearningContinuumHelper.checkLcElements = (lcData, currentCurriculum) => {
  let isValid = true
  let error = {}
  if (isEmpty(lcData.lcElements)) {
    isValid = false
    error['lcElements'] = 'LC elements are required'
  } else {
    Object.keys(lcData.lcElements).forEach((key) => {
      const find = currentCurriculum?.lcElements?.children?.find((el) => String(el._id) === String(key))
      if (!lcData.lcElements[key]) {
        isValid = false
        if (find) {
          error[find.name] = `This field is required`
        } else {
          error[key] = 'LC element is required'
        }
      }
    })
  }
  return {
    isValid,
    error
  }
}

LearningContinuumHelper.checkLevels = (lcData, currentCurriculum) => {
  let isValid = true
  let error = {}
  if (lcData.levels.length === 0) {
    isValid = false
    error['levels'] = 'Levels are required'
  } else {
    const find = currentCurriculum?.levels?.find((el) => el.position === 0)

    if (find && !lcData.levels?.find((l) => String(l.levelId) === String(find._id))) {
      isValid = false
      error['levels'] = 'Level 1 is required for the first level of curriculum'
    }

    let count = 0
    const group = {}
    let fff = false
    const levels = currentCurriculum?.levels
      ?.sort((a, b) => a.position - b.position)
      ?.map((el) => {
        const find = lcData.levels.find((l) => String(l.levelId) === String(el._id))
        return {
          levelId: el._id,
          definition: find ? find.definition : '',
          name: el.name
        }
      })
    for (let i = 1; i < levels.length - 1; i++) {
      if (levels[i].definition) {
        if (group[count]) {
          group[count].push(levels[i].levelId)
        } else {
          group[count] = [levels[i].levelId]
        }
      } else {
        if (group[count]?.length > 0) {
          if (!fff) {
            fff = true
          }
        }
        count++
      }
    }

    if (Object.keys(group).length > 1) {
      isValid = false
      error['levels'] = 'Each level must contain a descriptor before the continuum can be saved.'
    }
  }
  return {
    isValid,
    error
  }
}

export default LearningContinuumHelper
